import React, { useState, useEffect } from 'react'
import styles from './style.module.scss'
import { NameObj, NameSplitter } from 'pages/contacts/utils/NameSplitter'
import { useTranslation } from 'react-i18next'
import {
  modalContactDetailSharedContactId,
  modalContactDetailPersonal,
} from 'pages/contacts/components/ContactDetail'
import * as contactService from 'pages/contacts/modules/contactService'

import addIcon from 'assets/images/plus-24.svg'
import infoIcon from 'assets/images/toolbar-info-blue.svg'
import replaceIcon from 'assets/images/toolbar-sync-24.svg'
import mergeIcon from 'assets/images/toolbar-merge.svg'

import { getInitialAvatar } from 'utils/ui'

interface MergeContactProps {
  newContact?: any
  duplicates: any[]
  onCancel: () => void
  onMerge: (targetId: string, personalData: any) => void
  onReplace: (targetId: string, hasPhoto: boolean, personalData: any) => void
  onCreateNew: () => void
}

const MergeContact = ({
  /* newContact, */ duplicates,
  onCancel,
  onMerge,
  onReplace,
  onCreateNew,
}: MergeContactProps) => {
  const [showDuplicates, setShowDuplicates] = useState(duplicates?.length > 1)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [photoTags, setPhotoTags] = useState<React.ReactNode[]>([])
  const { t } = useTranslation()

  const inPhotoTags: React.ReactNode[] = []

  // 중복된 연락처가 없으면 바로 생성
  if (!duplicates || duplicates.length === 0) {
    onCreateNew()
    return null
  }

  useEffect(() => {
    for (let i = 0; i < duplicates.length; i++) {
      const orgContact = duplicates[i]
      const selectedContact = getContact(orgContact)
      if (selectedContact.isPersonal) {
        inPhotoTags.push(
          <div className={styles.defaultAvatar}>
            {getInitialAvatar(getName(selectedContact), selectedContact.emails?.[0]?.email)}
          </div>,
        )
        contactService.getPhoto(orgContact.serverId).then((result) => {
          if (result == null) return
          let p = result.photo
          if (typeof p === 'string' && !p.startsWith('data:image')) {
            p = `data:image/*;base64,${p}`
          }
          inPhotoTags[i] = <img src={p} alt={selectedContact.name} />
          setPhotoTags([...inPhotoTags])
        })
      } else {
        if (selectedContact.photo?.url) {
          inPhotoTags.push(<img src={selectedContact.photo.url} alt={selectedContact.name} />)
        } else {
          inPhotoTags.push(
            <div className={styles.defaultAvatar}>
              {getInitialAvatar(getName(selectedContact), selectedContact.emails?.[0]?.email)}
            </div>,
          )
        }
      }
    }
    setPhotoTags(inPhotoTags)
  }, [duplicates])

  const handleInfoClick = (index: number) => {
    const contact = duplicates[index]
    if (contact.contact?.isPersonal) {
      modalContactDetailPersonal(contact)
    } else {
      modalContactDetailSharedContactId(contact.id)
    }
  }

  const getContact = (contact: any) => {
    return contact.contact ? contact.contact : contact
  }

  const getName = (contact: any) => {
    const { person } = getContact(contact)
    if (person) {
      const nameObj = new NameObj({
        prefix: person.prefix,
        first_name: person.first_name,
        last_name: person.last_name,
        middle_name: person.middle_name,
        suffix: person.suffix,
      })
      const nameSplitter = new NameSplitter(navigator.language || 'en-US')
      nameSplitter.guessFullNameStyle(nameObj)
      return nameSplitter.joinWithName(nameObj, true, true)
    }
    return null
  }

  const getOrganization = (contact: any) => {
    const { organization } = getContact(contact)
    if (organization) {
      const { company, job_title: jobTitle, department } = organization
      const orgText = [company, jobTitle, department]
        .filter((item) => item != null && item.trim().length > 0)
        .join(' • ')
      return orgText
    }
    return null
  }

  if (showDuplicates) {
    return (
      <div className={styles.duplicatesFound}>
        <div className={styles.duplicatesContent}>
          <h2>{t('contact.merge.duplicatesFound')}</h2>
          <div className={styles.createNewOption} onClick={() => onCreateNew()}>
            <div className={styles.iconWrapper}>
              <img src={addIcon} alt="add" />
            </div>
            <div className={styles.optionContent}>
              <div className={styles.optionTitle}>{t('contact.merge.createNew')}</div>
              <div className={styles.optionDesc}>{t('contact.merge.createNewDesc')}</div>
            </div>
          </div>

          <p className={styles.duplicatesDesc}>{t('contact.merge.duplicatesDesc')}</p>
          <div className={styles.duplicatesList}>
            {duplicates.map((c, index) => {
              const contact = getContact(c)
              return (
                <div
                  key={index}
                  className={styles.contactItem}
                  onClick={() => {
                    setSelectedIndex(index)
                    setShowDuplicates(false)
                  }}
                >
                  <div className={styles.avatar}>{photoTags?.[index]}</div>
                  <div className={styles.contactInfo}>
                    <div className={styles.name}>{getName(contact)}</div>
                    <div className={styles.organization}>{getOrganization(contact)}</div>
                    <div className={styles.email}>{contact.emails?.[0]?.email}</div>
                  </div>
                  <div
                    className={styles.infoIcon}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleInfoClick(index)
                    }}
                  >
                    <img src={infoIcon} alt="info" />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <button className={styles.cancelButton} onClick={onCancel}>
          {t('common.button.cancel')}
        </button>
      </div>
    )
  }

  const orgContact = duplicates[selectedIndex]
  const selectedContact = getContact(orgContact)

  return (
    <div className={styles.mergeReplace}>
      <div className={styles.mergeReplaceContent}>
        <h2>{t('contact.merge.duplicatesFound')}</h2>
        <p className={styles.description}>{t('contact.merge.mergeReplaceDesc')}</p>

        <div className={styles.selectedContact}>
          <div className={styles.avatar}>{photoTags[selectedIndex]}</div>
          <div className={styles.contactInfo}>
            <div className={styles.name}>{getName(selectedContact)}</div>
            <div className={styles.organization}>{getOrganization(selectedContact)}</div>
            <div className={styles.email}>{selectedContact.emails?.[0]?.email}</div>
          </div>
          <div
            className={styles.infoIcon}
            onClick={() => {
              handleInfoClick(0)
            }}
          >
            <img src={infoIcon} alt="info" />
          </div>
        </div>
      </div>
      <div className={styles.options}>
        <div className={styles.option} onClick={onCreateNew}>
          <div className={styles.iconWrapper}>
            <img src={addIcon} alt="add" />
          </div>
          <div className={styles.optionContent}>
            <div className={styles.optionTitle}>{t('contact.merge.createNew')}</div>
            <div className={styles.optionDesc}>{t('contact.merge.createNewDesc')}</div>
          </div>
        </div>
        <div className={styles.separator} />
        <div
          className={styles.option}
          onClick={() => {
            onMerge(selectedContact.id, orgContact)
          }}
        >
          <div className={styles.iconWrapper}>
            <img src={mergeIcon} alt="merge" />
          </div>
          <div className={styles.optionContent}>
            <div className={styles.optionTitle}>{t('contact.merge.merge')}</div>
            <div className={styles.optionDesc}>{t('contact.merge.mergeDesc')}</div>
          </div>
        </div>
        <div className={styles.separator} />
        <div
          className={styles.option}
          onClick={() => {
            onReplace(selectedContact.id, !!selectedContact.photo?.url, orgContact)
          }}
        >
          <div className={styles.iconWrapper}>
            <img src={replaceIcon} alt="replace" />
          </div>
          <div className={styles.optionContent}>
            <div className={styles.optionTitle}>{t('contact.merge.replace')}</div>
            <div className={styles.optionDesc}>{t('contact.merge.replaceDesc')}</div>
          </div>
        </div>
        <div className={styles.separator} />
        <button className={styles.cancelButtonMerge} onClick={onCancel}>
          {t('common.button.cancel')}
        </button>
      </div>
    </div>
  )
}

export default MergeContact
